import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  splitTextByEntities,
  type HighlightEntity,
} from "@/components/artifacts/entityHighlight";

export interface HighlightedTextProps {
  text: string;
  entities: readonly HighlightEntity[];
  /**
   * Optional click handler — called when the user clicks a highlighted
   * employee/client. Receives the matched entity so the parent can pop a
   * "change binding" dialog.
   */
  onEntityClick?: (entity: HighlightEntity) => void;
}

/**
 * Inline component: walks `text` and wraps every occurrence of a bound
 * entity's display name in a coloured `<mark>` (yellow for employees,
 * blue for clients — matching the mockup palette).
 *
 * When `entities` is empty the text is rendered verbatim (no `<mark>`
 * nodes), so callers can pass this through unconditionally.
 */
export function HighlightedText({
  text,
  entities,
  onEntityClick,
}: HighlightedTextProps): ReactNode {
  const tokens = splitTextByEntities(text, entities);
  return (
    <>
      {tokens.map((tok, i) => {
        if (tok.kind === "plain") {
          return <Fragment key={i}>{tok.text}</Fragment>;
        }
        return (
          <EntityMark
            key={i}
            text={tok.text}
            entity={tok.entity}
            onClick={onEntityClick}
          />
        );
      })}
    </>
  );
}

interface EntityMarkProps {
  text: string;
  entity: HighlightEntity;
  onClick?: (entity: HighlightEntity) => void;
}

function EntityMark({ text, entity, onClick }: EntityMarkProps) {
  const { t } = useTranslation();
  const kind = entity.kind;
  const title = entity.pending
    ? t("meetings.card.matching.highlight.pendingTooltip")
    : kind === "employee"
      ? t("meetings.card.matching.highlight.employeeTooltip", {
          name: entity.name,
          confidence:
            entity.confidence === null ? "—" : entity.confidence.toFixed(2),
        })
      : t("meetings.card.matching.highlight.clientTooltip", {
          name: entity.name,
          confidence:
            entity.confidence === null ? "—" : entity.confidence.toFixed(2),
        });
  const cls =
    kind === "employee"
      ? "rounded-sm bg-amber-100 px-1 font-semibold text-amber-900 underline decoration-dotted decoration-amber-600"
      : "rounded-sm bg-blue-100 px-1 font-semibold text-blue-900 underline decoration-dotted decoration-blue-600";
  if (onClick === undefined) {
    return (
      <mark
        className={cls}
        title={title}
        data-testid={`highlight-${kind}-${entity.entityId}`}
        data-entity-kind={kind}
        data-entity-id={entity.entityId}
      >
        {text}
      </mark>
    );
  }
  return (
    <mark
      className={`${cls} cursor-pointer`}
      title={title}
      data-testid={`highlight-${kind}-${entity.entityId}`}
      data-entity-kind={kind}
      data-entity-id={entity.entityId}
      role="button"
      tabIndex={0}
      onClick={() => onClick(entity)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(entity);
        }
      }}
    >
      {text}
    </mark>
  );
}
